import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';

import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { TranslatePipe } from '@core/i18n-legacy';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { getCandidateScope, SharingContainerSelectors } from '@core/sharing-container-data';
import type { SharingContainerApi } from '@core/sharing-container-types';
import { DialogData, DialogRef, RtStatefulComponent } from '@core/user-interface-legacy';

import type { CandidateSharingDialogComponentData } from './candidate-sharing-dialog-component.types';
import { CandidateSharingDialogService } from './candidate-sharing-dialog.service';
import type { State } from './candidate-sharing-dialog.types';
import { CandidateSharingAssignmentsComponent } from '../candidate-sharing-assignments/candidate-sharing-assignments.component';

@Component({
  selector: 'rt-candidate-sharing-dialog',
  templateUrl: './candidate-sharing-dialog.component.html',
  styleUrls: ['./candidate-sharing-dialog.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CandidateSharingAssignmentsComponent, TranslatePipe],
})
export class CandidateSharingDialogComponent extends RtStatefulComponent<State> {
  private readonly dialog = inject(DialogRef);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly containerService = inject(CandidateSharingDialogService);
  protected readonly cdRef: ChangeDetectorRef;

  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    const cdRef = inject(ChangeDetectorRef);
    const data = inject<CandidateSharingDialogComponentData>(DialogData);

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      candidateId: data.candidateId,
      assignments: [],
      heading: data.heading,
      subheading: data.subheading,
      submitText: data.submitText,
      activeContainers: [],
      loaded: false,
      isLoading: false,
      deleteMode: data.deleteMode ? data.deleteMode : false,
      dataScope: data.deleteMode ? getCandidateScope(data.candidateId) : (data.dataScope ?? ''),
    });
  }

  public ngOnInit(): void {
    this.containerService.fetchContainers(this.state);

    this.store
      .select(SharingContainerSelectors.isLoaded({ scope: this.state.dataScope }))
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(loaded => this.setState({ ...this.state, loaded }));

    combineLatest([
      this.store.select(
        SharingContainerSelectors.candidateEntitiesActive({
          candidateId: this.state.candidateId,
          scope: this.state.dataScope,
        }),
      ),
      this.store.select(
        SharingContainerSelectors.entitiesWithReferences({ scope: this.state.dataScope }),
      ),
    ])
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(([candidateActiveContainers, containers]) => {
        this.setState({
          ...this.state,
          assignments: candidateActiveContainers.map(container => container.id),
          activeContainers: containers.filter(container => container.active),
        });
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public submitClick(newAssignments: number[]): void {
    this.toggleSpinner(true);
    const changes = this.containerService.getAssignmentsChanges(
      newAssignments,
      this.state.assignments,
      this.state.deleteMode,
    );

    if (changes.created.length === 0 && changes.deleted.length === 0) {
      this.dialog.close();
      return;
    }

    let createdContainers: SharingContainerApi[] = [];

    if (!this.state.deleteMode) {
      createdContainers = this.containerService.saveCreatedContainers(changes, this.state);
    }

    const deletedContainers = this.containerService.saveDeletedContainers(changes, this.state);
    this.containerService
      .awaitSaveResult(this.toggleSpinner.bind(this), false)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() =>
        this.dialog.close(
          this.state.deleteMode ? deletedContainers.length : createdContainers.length,
        ),
      );
  }

  public cancelClick(): void {
    this.dialog.close();
  }

  private toggleSpinner(enable: boolean): void {
    this.setState({
      ...this.state,
      isLoading: enable,
    });
  }
}
